/**
 * BHERMES Metaverse World Map (Phase 3)
 * Interactive 3D globe showing all protocol-registered hotels
 * Hotel markers scale by SBT reputation score
 * Route: /metaverse
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Globe, Building2, Sparkles, Share2, Eye, Loader2,
  ChevronRight, Shield, Boxes, ExternalLink,
} from 'lucide-react';
import { BACKEND_URL, API } from '@/lib/backendUrl';


/* ═══════════════════════════════════════
   3D GLOBE BUILDER
   ═══════════════════════════════════════ */

function latLngToVector3(lat, lng, radius) {
  const phi = (90 - lat) * (Math.PI / 180);
  const theta = (lng + 180) * (Math.PI / 180);
  return new THREE.Vector3(
    -(radius * Math.sin(phi) * Math.cos(theta)),
    radius * Math.cos(phi),
    radius * Math.sin(phi) * Math.sin(theta),
  );
}

function buildGlobe(scene) {
  // Globe sphere
  const globeGeo = new THREE.SphereGeometry(5, 64, 64);
  const globeMat = new THREE.MeshStandardMaterial({
    color: 0x0a1628,
    roughness: 0.8,
    metalness: 0.2,
    transparent: true,
    opacity: 0.92,
  });
  const globe = new THREE.Mesh(globeGeo, globeMat);
  globe.name = 'globe';
  scene.add(globe);

  // Grid lines (latitude/longitude)
  const gridMat = new THREE.LineBasicMaterial({ color: 0x1a2a4a, transparent: true, opacity: 0.35 });
  for (let i = -80; i <= 80; i += 20) {
    const pts = [];
    for (let j = 0; j <= 360; j += 4) {
      pts.push(latLngToVector3(i, j, 5.02));
    }
    const line = new THREE.Line(new THREE.BufferGeometry().setFromPoints(pts), gridMat);
    scene.add(line);
  }
  for (let j = 0; j < 360; j += 30) {
    const pts = [];
    for (let i = -90; i <= 90; i += 4) {
      pts.push(latLngToVector3(i, j, 5.02));
    }
    const line = new THREE.Line(new THREE.BufferGeometry().setFromPoints(pts), gridMat);
    scene.add(line);
  }

  // Atmosphere glow
  const atmosGeo = new THREE.SphereGeometry(5.2, 64, 64);
  const atmosMat = new THREE.ShaderMaterial({
    transparent: true,
    side: THREE.BackSide,
    uniforms: {
      glowColor: { value: new THREE.Color(0xF7931A) },
    },
    vertexShader: `
      varying vec3 vNormal;
      void main() {
        vNormal = normalize(normalMatrix * normal);
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: `
      uniform vec3 glowColor;
      varying vec3 vNormal;
      void main() {
        float intensity = pow(0.65 - dot(vNormal, vec3(0.0, 0.0, 1.0)), 3.0);
        gl_FragColor = vec4(glowColor, intensity * 0.25);
      }
    `,
  });
  scene.add(new THREE.Mesh(atmosGeo, atmosMat));

  // Lighting
  scene.add(new THREE.AmbientLight(0xffffff, 0.35));
  const dirLight = new THREE.DirectionalLight(0xffffff, 0.8);
  dirLight.position.set(10, 8, 5);
  scene.add(dirLight);
  const pointLight = new THREE.PointLight(0xF7931A, 0.4, 30);
  pointLight.position.set(-8, 5, -5);
  scene.add(pointLight);

  // Stars
  const starGeo = new THREE.BufferGeometry();
  const starVerts = [];
  for (let i = 0; i < 2000; i++) {
    starVerts.push(
      (Math.random() - 0.5) * 200,
      (Math.random() - 0.5) * 200,
      (Math.random() - 0.5) * 200,
    );
  }
  starGeo.setAttribute('position', new THREE.Float32BufferAttribute(starVerts, 3));
  scene.add(new THREE.Points(starGeo, new THREE.PointsMaterial({ color: 0xffffff, size: 0.15, transparent: true, opacity: 0.6 })));

  return globe;
}

function addMarker(scene, hotel, markerGroup) {
  const score = hotel.trust_score || 0;
  const baseSize = 0.06;
  const scaledSize = baseSize + (score / 100) * 0.12;
  const hasDigitalTwin = hotel.digital_twins > 0;

  const pos = latLngToVector3(hotel.lat, hotel.lng, 5.05);

  // Marker dot
  const color = score >= 80 ? 0xF7931A : score >= 50 ? 0x4ade80 : 0x6b7280;
  const dotGeo = new THREE.SphereGeometry(scaledSize, 16, 16);
  const dotMat = new THREE.MeshStandardMaterial({
    color,
    emissive: color,
    emissiveIntensity: 0.8,
    roughness: 0.3,
    metalness: 0.5,
  });
  const dot = new THREE.Mesh(dotGeo, dotMat);
  dot.position.copy(pos);
  dot.userData = { hotel_id: hotel.hotel_id, type: 'marker' };
  markerGroup.add(dot);

  // Pulse ring
  const ringGeo = new THREE.RingGeometry(scaledSize * 1.5, scaledSize * 2.2, 32);
  const ringMat = new THREE.MeshBasicMaterial({
    color,
    transparent: true,
    opacity: 0.3,
    side: THREE.DoubleSide,
  });
  const ring = new THREE.Mesh(ringGeo, ringMat);
  ring.position.copy(pos);
  ring.lookAt(0, 0, 0);
  ring.userData = { pulse: true, baseOpacity: 0.3 };
  markerGroup.add(ring);

  // Vertical beam for high-score hotels
  if (score >= 70) {
    const beamGeo = new THREE.CylinderGeometry(0.008, 0.008, 0.6, 8);
    const beamMat = new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.4 });
    const beam = new THREE.Mesh(beamGeo, beamMat);
    const normal = pos.clone().normalize();
    beam.position.copy(pos).add(normal.multiplyScalar(0.3));
    beam.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), pos.clone().normalize());
    markerGroup.add(beam);
  }

  // Digital twin indicator ring
  if (hasDigitalTwin) {
    const twinRingGeo = new THREE.RingGeometry(scaledSize * 2.8, scaledSize * 3.2, 32);
    const twinRingMat = new THREE.MeshBasicMaterial({
      color: 0xF7931A,
      transparent: true,
      opacity: 0.15,
      side: THREE.DoubleSide,
    });
    const twinRing = new THREE.Mesh(twinRingGeo, twinRingMat);
    twinRing.position.copy(pos);
    twinRing.lookAt(0, 0, 0);
    markerGroup.add(twinRing);
  }
}

/* ═══════════════════════════════════════
   HOTEL TOOLTIP
   ═══════════════════════════════════════ */

function HotelTooltip({ hotel, position, onEnter }) {
  if (!hotel) return null;
  const score = hotel.trust_score || 0;
  const scoreColor = score >= 80 ? 'text-[#F7931A]' : score >= 50 ? 'text-emerald-400' : 'text-gray-400';

  return (
    <div
      className="fixed z-50 pointer-events-auto"
      style={{ left: position.x, top: position.y, transform: 'translate(-50%, -120%)' }}
      data-testid="hotel-tooltip"
    >
      <div
        className="bg-[#0c1020]/95 backdrop-blur-xl border border-white/10 rounded-xl p-3 min-w-[220px] cursor-pointer hover:border-[#F7931A]/30 transition-colors"
        onClick={() => onEnter(hotel.hotel_id)}
      >
        <div className="flex items-center justify-between mb-2">
          <p className="text-sm font-medium text-white">{hotel.name}</p>
          <ChevronRight className="h-3.5 w-3.5 text-gray-500" />
        </div>
        <p className="text-[10px] text-gray-500 mb-2">{hotel.city}, {hotel.country}</p>

        <div className="flex items-center gap-3 text-[10px]">
          <div className="flex items-center gap-1">
            <Shield className={`h-3 w-3 ${scoreColor}`} />
            <span className={scoreColor}>{score}</span>
          </div>
          {hotel.digital_twins > 0 && (
            <div className="flex items-center gap-1 text-[#F7931A]">
              <Boxes className="h-3 w-3" />
              <span>{hotel.digital_twins} twin{hotel.digital_twins > 1 ? 's' : ''}</span>
            </div>
          )}
          {hotel.has_wallet && (
            <Badge variant="outline" className="text-[8px] h-4 border-emerald-500/30 text-emerald-400">
              On-chain
            </Badge>
          )}
        </div>

        <div className="mt-2 pt-2 border-t border-white/5 flex items-center gap-1 text-[10px] text-[#F7931A]">
          <Eye className="h-3 w-3" /> Enter Metaverse
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════
   MAIN PAGE
   ═══════════════════════════════════════ */

export default function MetaverseWorldMapPage() {
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const [markers, setMarkers] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [hoveredHotel, setHoveredHotel] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });

  // Refs for Three.js objects
  const sceneRef = useRef(null);
  const cameraRef = useRef(null);
  const rendererRef = useRef(null);
  const markerGroupRef = useRef(null);
  const raycasterRef = useRef(new THREE.Raycaster());
  const mouseRef = useRef(new THREE.Vector2());
  const markersDataRef = useRef([]);

  // Fetch world map data
  useEffect(() => {
    fetch(`${API}/api/metaverse/world-map`)
      .then(r => r.json())
      .then(data => {
        if (data.success) {
          setMarkers(data.markers || []);
          setStats(data.stats || {});
          markersDataRef.current = data.markers || [];
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  // Build 3D globe
  useEffect(() => {
    if (loading || !containerRef.current) return;
    const container = containerRef.current;
    const w = container.clientWidth;
    const h = container.clientHeight;
    if (w === 0 || h === 0) return;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x040810);
    sceneRef.current = scene;

    const camera = new THREE.PerspectiveCamera(45, w / h, 0.1, 500);
    camera.position.set(0, 2, 14);
    cameraRef.current = camera;

    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(w, h);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    container.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.05;
    controls.minDistance = 7;
    controls.maxDistance = 25;
    controls.enablePan = false;
    controls.autoRotate = true;
    controls.autoRotateSpeed = 0.3;
    controls.update();

    buildGlobe(scene);

    // Add markers
    const markerGroup = new THREE.Group();
    markerGroup.name = 'markers';
    markers.forEach(h => addMarker(scene, h, markerGroup));
    scene.add(markerGroup);
    markerGroupRef.current = markerGroup;

    // Animation
    let animId;
    let time = 0;
    const animate = () => {
      animId = requestAnimationFrame(animate);
      time += 0.02;
      controls.update();

      // Pulse rings
      markerGroup.children.forEach(child => {
        if (child.userData.pulse) {
          child.material.opacity = child.userData.baseOpacity * (0.5 + 0.5 * Math.sin(time * 2));
        }
      });

      renderer.render(scene, camera);
    };
    animate();

    // Resize
    const onResize = () => {
      const ww = container.clientWidth;
      const hh = container.clientHeight;
      if (ww > 0 && hh > 0) {
        camera.aspect = ww / hh;
        camera.updateProjectionMatrix();
        renderer.setSize(ww, hh);
      }
    };
    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('resize', onResize);
      cancelAnimationFrame(animId);
      controls.dispose();
      renderer.dispose();
      if (container.contains(renderer.domElement)) container.removeChild(renderer.domElement);
    };
  }, [loading, markers]);

  // Mouse interaction — hover to show tooltip, click to enter
  const handleMouseMove = useCallback((e) => {
    if (!rendererRef.current || !cameraRef.current || !markerGroupRef.current) return;
    const rect = rendererRef.current.domElement.getBoundingClientRect();
    mouseRef.current.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
    mouseRef.current.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;

    raycasterRef.current.setFromCamera(mouseRef.current, cameraRef.current);
    const intersects = raycasterRef.current.intersectObjects(markerGroupRef.current.children, false);
    const hit = intersects.find(i => i.object.userData.hotel_id);

    if (hit) {
      const hid = hit.object.userData.hotel_id;
      const hotel = markersDataRef.current.find(m => m.hotel_id === hid);
      if (hotel) {
        setHoveredHotel(hotel);
        setTooltipPos({ x: e.clientX, y: e.clientY });
      }
    } else {
      setHoveredHotel(null);
    }
  }, []);

  const handleClick = useCallback((e) => {
    if (!rendererRef.current || !cameraRef.current || !markerGroupRef.current) return;
    const rect = rendererRef.current.domElement.getBoundingClientRect();
    const mouse = new THREE.Vector2(
      ((e.clientX - rect.left) / rect.width) * 2 - 1,
      -((e.clientY - rect.top) / rect.height) * 2 + 1,
    );
    raycasterRef.current.setFromCamera(mouse, cameraRef.current);
    const intersects = raycasterRef.current.intersectObjects(markerGroupRef.current.children, false);
    const hit = intersects.find(i => i.object.userData.hotel_id);
    if (hit) {
      navigate(`/metaverse/${hit.object.userData.hotel_id}`);
    }
  }, [navigate]);

  const enterHotel = useCallback((hotelId) => {
    navigate(`/metaverse/${hotelId}`);
  }, [navigate]);

  if (loading) {
    return (
      <div className="h-screen bg-[#040810] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-[#F7931A] mx-auto mb-3" />
          <p className="text-sm text-gray-500">Loading Metaverse World Map...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-[#040810] flex flex-col overflow-hidden" data-testid="metaverse-world-map">
      {/* Top Bar */}
      <div className="h-14 border-b border-white/5 bg-[#040810]/80 backdrop-blur-xl flex items-center justify-between px-6 z-30 shrink-0">
        <div className="flex items-center gap-3">
          <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-[#F7931A] to-[#B08D57] flex items-center justify-center">
            <Globe className="h-4 w-4 text-white" />
          </div>
          <div>
            <p className="text-sm font-semibold text-white tracking-wide">BHERMES Metaverse</p>
            <p className="text-[10px] text-gray-500">World Map — Phase 3</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Badge variant="outline" className="border-[#F7931A]/30 text-[#F7931A] text-[10px] gap-1">
            <Sparkles className="h-3 w-3" /> Live
          </Badge>
          <Button
            size="sm"
            variant="ghost"
            className="text-xs text-gray-400 hover:text-white gap-1"
            onClick={() => navigate('/creator-marketplace')}
            data-testid="creator-marketplace-link"
          >
            <Sparkles className="h-3.5 w-3.5" /> Creators
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="text-xs text-gray-400 hover:text-white gap-1"
            onClick={() => navigate('/protocol')}
            data-testid="protocol-link"
          >
            <ExternalLink className="h-3.5 w-3.5" /> Protocol
          </Button>
        </div>
      </div>

      {/* Globe + Overlays */}
      <div className="flex-1 relative">
        {/* 3D Globe Canvas */}
        <div
          ref={containerRef}
          className="absolute inset-0 cursor-crosshair"
          onMouseMove={handleMouseMove}
          onClick={handleClick}
          data-testid="globe-canvas"
        />

        {/* Stats Panel — Bottom Left */}
        <div className="absolute bottom-6 left-6 z-20" data-testid="stats-panel">
          <div className="bg-[#0c1020]/80 backdrop-blur-xl border border-white/5 rounded-xl p-4 space-y-3 min-w-[200px]">
            <p className="text-[10px] uppercase tracking-widest text-gray-500 mb-2">Protocol Stats</p>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-gray-400">
                <Building2 className="h-3.5 w-3.5" />
                <span className="text-xs">Hotels</span>
              </div>
              <span className="text-sm font-semibold text-white" data-testid="stat-hotels">{stats.total_hotels || 0}</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-gray-400">
                <Boxes className="h-3.5 w-3.5" />
                <span className="text-xs">Digital Twins</span>
              </div>
              <span className="text-sm font-semibold text-[#F7931A]" data-testid="stat-twins">{stats.total_digital_twins || 0}</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-gray-400">
                <Share2 className="h-3.5 w-3.5" />
                <span className="text-xs">Shares</span>
              </div>
              <span className="text-sm font-semibold text-emerald-400" data-testid="stat-shares">{stats.total_shares || 0}</span>
            </div>
          </div>
        </div>

        {/* Hotel List — Right Side */}
        <div className="absolute top-4 right-6 bottom-4 w-72 z-20 overflow-y-auto" data-testid="hotel-list">
          <div className="bg-[#0c1020]/60 backdrop-blur-xl border border-white/5 rounded-xl p-3 space-y-2">
            <p className="text-[10px] uppercase tracking-widest text-gray-500 mb-2 px-1">Registered Hotels</p>
            {markers.map(h => {
              const score = h.trust_score || 0;
              const scoreColor = score >= 80 ? 'text-[#F7931A]' : score >= 50 ? 'text-emerald-400' : 'text-gray-500';
              return (
                <button
                  key={h.hotel_id}
                  className="w-full text-left p-2.5 rounded-lg hover:bg-white/5 transition-colors group"
                  onClick={() => enterHotel(h.hotel_id)}
                  data-testid={`hotel-item-${h.hotel_id}`}
                >
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-medium text-gray-300 group-hover:text-white transition-colors truncate pr-2">
                      {h.name}
                    </p>
                    <div className="flex items-center gap-1 shrink-0">
                      <Shield className={`h-3 w-3 ${scoreColor}`} />
                      <span className={`text-[10px] font-semibold ${scoreColor}`}>{score}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-[10px] text-gray-600">{h.city}, {h.country}</span>
                    {h.digital_twins > 0 && (
                      <Badge className="text-[8px] h-3.5 bg-[#F7931A]/10 text-[#F7931A] border-0 px-1">
                        {h.digital_twins} twin{h.digital_twins > 1 ? 's' : ''}
                      </Badge>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Legend — Bottom Right */}
        <div className="absolute bottom-6 right-6 z-20" data-testid="legend">
          <div className="bg-[#0c1020]/60 backdrop-blur-xl border border-white/5 rounded-lg p-3 space-y-1.5 w-52">
            <p className="text-[10px] uppercase tracking-widest text-gray-500 mb-1">Legend</p>
            <div className="flex items-center gap-2 text-[10px]">
              <div className="h-2.5 w-2.5 rounded-full bg-[#F7931A] shadow-[0_0_6px_#F7931A]" />
              <span className="text-gray-400">Trust Score 80+</span>
            </div>
            <div className="flex items-center gap-2 text-[10px]">
              <div className="h-2 w-2 rounded-full bg-emerald-400 shadow-[0_0_4px_#4ade80]" />
              <span className="text-gray-400">Trust Score 50-79</span>
            </div>
            <div className="flex items-center gap-2 text-[10px]">
              <div className="h-1.5 w-1.5 rounded-full bg-gray-500" />
              <span className="text-gray-400">Trust Score &lt; 50</span>
            </div>
            <div className="flex items-center gap-2 text-[10px] pt-1 border-t border-white/5">
              <div className="h-2.5 w-2.5 rounded-full border border-[#F7931A]/40" />
              <span className="text-gray-400">Has Digital Twin</span>
            </div>
          </div>
        </div>

        {/* Tooltip */}
        <HotelTooltip hotel={hoveredHotel} position={tooltipPos} onEnter={enterHotel} />
      </div>
    </div>
  );
}
